import { Form, message } from 'antd';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { request } from '../../../apis';
import { useDispatch } from 'react-redux';
import { getTransactingUsersApi, suspendUserApi, getStatusesApi, getHrCompaniesGroupApi } from '../../../utils/apisConsts';
import { ReusableDataTable } from '@operations/shared';
import helper from '../../../utils/helper';
import { errorMessage } from '../../../utils/commonStringsConsts';
import { IUser, ISummary, IFilters, Region } from '../../../utils/commonInterfaces';
import { exportToCSV } from './exportHRTransactingUsers';
import { BRAND, SEGMENTS, Segment, TRANSACTING_USERS_EXPORT_NUMBER_COLUMNS, allowedReasons } from './constants';
import { routeSearchTerm, statusToColor, mapUserToExportRow } from './utils';
import InsightStyles from './components/InsightStyles';
import PageHeader from './components/PageHeader';
import StatsCards from './components/StatsCards';
import FiltersBar from './components/FiltersBar';
import { getUserColumns } from './components/userColumns';
import SuspendModal, { SuspendSuccessInfo } from './components/SuspendModal';
import UserDetailsDrawer from './components/UserDetailsDrawer';
import { getRegion } from '../../../components/regionWrapper';

export default function ViewTransacting() {
    const region = getRegion();
    const [form] = Form.useForm();
    const [suspendForm] = Form.useForm();
    const [loadingData, setLoadingData] = useState(false);
    const [statuses, setStatuses] = useState<any[]>([]);
    const [users, setUsers] = useState<IUser[]>([]);
    const [summary, setSummary] = useState<ISummary | null>(null);
    const [canResetFilter, setCanResetFilter] = useState<boolean>(false);
    const dispatch = useDispatch();
    const [visibleModal, setVisibleModal] = useState(false);
    const [suspensionReasons, setSuspensionReasons] = useState<any[]>([]);
    const [loadingReasons, setLoadingReasons] = useState(false);
    const [suspending, setSuspending] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [selectedUserID, setSelectedUserID] = useState<string | null>(null);
    const [suspendModalView, setSuspendModalView] = useState<'form' | 'success'>('form');
    const [suspendSuccessInfo, setSuspendSuccessInfo] = useState<SuspendSuccessInfo | null>(null);
    const [animatedCount, setAnimatedCount] = useState({ users: 0, amount: 0 });
    const [activeSegment, setActiveSegment] = useState<string>('all');
    const [detailsUser, setDetailsUser] = useState<IUser | null>(null);
    const [hrCompanyGroupsInfo, setHrCompanyGroupsInfo] = useState<any>(null);

    const statusSlices = useMemo(() => {
        const counts = new Map<string, number>();
        Object.entries(summary?.statusBreakdown ?? {}).forEach(([label, value]) => {
            counts.set(label, value);
        });
        return Array.from(counts.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([label, value]) => ({ label, value, color: statusToColor(label) }));
    }, [summary]);

    // Yellow trend line — outstanding-amount shape across the current page of users.
    const trendPoints = useMemo(() => {
        const pts = (users ?? []).map((u: any) => Number(u?.outstandingAmount) || 0);
        if (pts.length >= 2) return pts;
        if (pts.length === 1) return [0, pts[0]];
        return [4, 9, 6, 12, 8, 15, 11, 18];
    }, [users]);

    const selectedUser = useMemo(
        () => users?.find?.((user: IUser) => user?.userID === selectedUserID) ?? null,
        [users, selectedUserID]
    );

    const getSuspensionReasons = useCallback(async () => {
        try {
            setLoadingReasons(true);
            const res: any = await helper.getSuspensionReasons();
            if (res?.data?.status === 200) {
                const filteredReasons = (res?.data?.data ?? [])?.filter?.((reason: any) => {
                    const reasonNameLower = reason?.name?.toLowerCase();
                    return allowedReasons?.some(allowed => allowed?.toLowerCase() === reasonNameLower);
                });

                setSuspensionReasons(filteredReasons);
            } else {
                message.error(errorMessage);
            }
        } catch (error) {
            message.error(errorMessage);
        } finally {
            setLoadingReasons(false);
        }
    }, []);

    const toggleSuspendModal = useCallback(async (id: string) => {
        setSelectedUserID(id);
        setSuspendModalView('form');
        setSuspendSuccessInfo(null);
        setVisibleModal(true);
        suspendForm.resetFields();
        await getSuspensionReasons();
    }, [suspendForm, getSuspensionReasons]);

    useEffect(() => {
        if (visibleModal && suspensionReasons?.length > 0) {
            const hrRequest = suspensionReasons?.find?.((r: any) => r?.name === 'HR Request');
            if (hrRequest) {
                suspendForm.setFieldsValue({ reasonID: hrRequest?.id });
            }
        }
    }, [visibleModal, suspensionReasons, suspendForm]);

    const columns = useMemo(() => getUserColumns(toggleSuspendModal), [toggleSuspendModal]);

    // Open the right-side details drawer for a clicked user row.
    const onRow = useCallback((record: IUser) => ({
        onClick: () => setDetailsUser(record),
    }), []);

    // Suspend triggered from inside the drawer — keep the drawer open and layer the modal on top.
    const handleSuspendFromDrawer = useCallback((id: string) => {
        toggleSuspendModal(id);
    }, [toggleSuspendModal]);

    useEffect(() => {
        getData();
        getStatuses();
        getCompanies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Animate numbers counting up
    useEffect(() => {
        if (summary) {
            const targetUsers = summary.transactingUsersCount ?? 0;
            const targetAmount = summary.totalOutstandingAmount ?? 0;

            const duration = 3000; // 3 seconds — slow enough to be clearly visible
            const steps = 90;
            const stepTime = duration / steps;

            let currentStep = 0;
            const usersInterval = setInterval(() => {
                currentStep++;
                const progress = currentStep / steps;
                const easeOut = 1 - Math.pow(1 - progress, 3); // Ease out cubic
                setAnimatedCount(prev => ({
                    ...prev,
                    users: Math.floor(targetUsers * easeOut),
                    amount: Math.floor(targetAmount * easeOut),
                }));

                if (currentStep >= steps) {
                    clearInterval(usersInterval);
                    setAnimatedCount({ users: targetUsers, amount: targetAmount });
                }
            }, stepTime);

            return () => clearInterval(usersInterval);
        }
    }, [summary]);

    const getData = async (
        page = pagination.current,
        pageSize = pagination.pageSize,
        filters: IFilters = {}
    ) => {
        setLoadingData(true);
        try {
            const resData: any = await dispatch(
                request({
                    url: getTransactingUsersApi,
                    method: "GET",
                    params: {
                        page,
                        perPage: pageSize,
                        staff_id: filters?.staffID,
                        phone: filters?.phoneNumber,
                        statusID: filters?.statusID,
                        nationalID: filters?.nationalID,
                        companyIDs: filters?.companyIDs || null,
                        ...(region === Region.EG ? { bankAccountNumber: filters?.bankAccountNumber } : {}),
                    }
                })
            );

            if (resData?.status === 200) {
                const { users, summary, pagination } = resData?.data?.data;

                setUsers(users ?? []);
                setSummary(summary ?? null);
                setPagination({
                    current: pagination?.currentPage,
                    pageSize: pagination?.itemsPerPage,
                    total: pagination?.totalItems,
                });
            }
        } catch (error) {
            message.error(errorMessage);
        } finally {
            setLoadingData(false);
        }
    };

    const getStatuses = async () => {
        try {
            const resData: any = await dispatch(
                request({
                    url: getStatusesApi,
                    method: "GET",
                })
            );
            if (resData?.data?.status === 200) {
                setStatuses(resData?.data?.reasons ?? []);
            } else {
                message.error(errorMessage);
            }
        } catch (error) {
            message.error(errorMessage);
        }
    };

    const getCompanies = async () => {
        try {
            const resData: any = await dispatch(
                request({
                    url: getHrCompaniesGroupApi,
                    method: "GET",
                })
            );
            if (resData?.data?.status === 200) {
                setHrCompanyGroupsInfo(resData?.data?.data ?? null);
            } else {
                message.error(errorMessage);
            }
        } catch (error) {
            message.error(errorMessage);
        }
    };

    const handleTableChange = (paginationConfig: any) => {
        const formValues = form.getFieldsValue();

        const filters: IFilters = canResetFilter
            ? {
                ...routeSearchTerm(formValues?.search),
                statusID: formValues?.status || null,
                companyIDs: formValues?.companyIDs?.length ? formValues.companyIDs.join(',') : null,
            }
            : {};

        setPagination({
            current: paginationConfig.current,
            pageSize: paginationConfig.pageSize,
            total: paginationConfig.total,
        });

        getData(paginationConfig.current, paginationConfig.pageSize, filters);
    };

    const handleSearch = (values: any) => {
        getData(1, pagination.pageSize, {
            ...routeSearchTerm(values.search),
            statusID: values.status,
            companyIDs: values.companyIDs?.length ? values.companyIDs.join(',') : null,
        });
        setCanResetFilter(true);
    };

    const handleClearFilter = async () => {
        form.resetFields();
        const pageSize = pagination.pageSize || 10;
        setPagination({
            current: 1,
            pageSize,
            total: pagination.total || 0,
        });
        await getData(1, pageSize, {
            companyIDs: null,
        });
        setCanResetFilter(false);
        setActiveSegment('all');
    };

    // Map a selected dropdown statusID back to a segment tab. Returns '' when the
    // status has no matching tab (e.g. Blocked/Resigned) so no tab stays highlighted.
    const resolveSegmentKey = (statusId: any): string => {
        if (statusId === undefined || statusId === null) return 'all';
        const status = statuses.find((s: any) => s?.id === statusId);
        const name = (status?.name || '').toLowerCase();
        const seg = SEGMENTS.find(sg => sg.keywords?.some(k => name.includes(k)));
        return seg ? seg.key : '';
    };

    // Keep the segment tabs in sync when the user picks from the status dropdown.
    const handleStatusSelectChange = (statusId: any) => {
        setActiveSegment(resolveSegmentKey(statusId));
    };

    // Segment tabs (All / Pending / Approved / Inactive). Resolve the tab to a
    // live statusID from the loaded statuses list, then refetch from the backend.
    const handleSegmentChange = (segment: Segment) => {
        setActiveSegment(segment.key);
        const pageSize = pagination.pageSize || 10;

        if (!segment.keywords) {
            form.setFieldsValue({ status: undefined });
            setPagination(prev => ({ ...prev, current: 1 }));
            getData(1, pageSize, {
                ...routeSearchTerm(form.getFieldValue('search')),
                statusID: null,
                companyIDs: form.getFieldValue('companyIDs')?.join(',') || null,
            });
            return;
        }

        const matched = statuses.find((s: any) => {
            const name = (s?.name || '').toLowerCase();
            return segment.keywords!.some(k => name.includes(k));
        });

        form.setFieldsValue({ status: matched?.id });
        setCanResetFilter(true);
        setPagination(prev => ({ ...prev, current: 1 }));
        getData(1, pageSize, {
            ...routeSearchTerm(form.getFieldValue('search')),
            statusID: matched?.id ?? null,
            companyIDs: form.getFieldValue('companyIDs')?.join(',') || null,
        });
    };

    const getAllDataForExport = async () => {
        try {
            const formValues = form.getFieldsValue();
            const routed = canResetFilter ? routeSearchTerm(formValues?.search) : { staffID: null, phoneNumber: null, nationalID: null, bankAccountNumber: null };
            const resData: any = await dispatch(
                request({
                    url: getTransactingUsersApi,
                    method: "GET",
                    params: {
                        staff_id: routed.staffID,
                        phone: routed.phoneNumber,
                        statusID: canResetFilter ? formValues?.status || null : null,
                        national_id: routed.nationalID,
                        companyIDs: formValues?.companyIDs?.length ? formValues?.companyIDs.join(',') : null,
                        ...(region === Region.EG ? { bankAccountNumber: routed.bankAccountNumber } : {}),
                    }
                })
            );

            if (resData?.data?.status === 200) {
                const { users } = resData?.data?.data;
                return (users ?? []).map(mapUserToExportRow);
            }
            message.error(errorMessage);
        } catch (error) {
            message.error(errorMessage);
        }
    };

    const handleExport = () => exportToCSV(
        undefined as any,
        'transactingUsers',
        getAllDataForExport,
        undefined,
        TRANSACTING_USERS_EXPORT_NUMBER_COLUMNS,
        true,
    );

    const handleSuspendUser = async (values?: any) => {
        try {
            const formValues = values || await suspendForm.validateFields();
            setSuspending(true);
            const resData: any = await dispatch(
                request({
                    url: `${suspendUserApi}${selectedUserID}`,
                    method: "PATCH",
                    params: {
                        reasonID: formValues?.reasonID,
                    }
                })
            );
            if (resData?.data?.status === 200) {
                const outstandingAmount =
                    resData?.data?.data?.outstandingAmount ??
                    selectedUser?.outstandingAmount;
                setSuspendSuccessInfo({
                    name: selectedUser?.name ?? 'N/A',
                    staff_id: selectedUser?.staff_id ?? 'N/A',
                    outstandingAmount,
                });
                setSuspendModalView('success');
                suspendForm.resetFields();
                // Keep the drawer open but reflect the new suspended state so it isn't stale.
                setDetailsUser(prev =>
                    prev && prev.userID === selectedUserID
                        ? { ...prev, isSuspended: true }
                        : prev
                );
            } else {
                message.error(resData?.data?.message || errorMessage);
            }
        } catch (error: any) {
            if (error?.errorFields) {
                return;
            }
            message.error(errorMessage);
        } finally {
            setSuspending(false);
        }
    };

    const handleModalOk = () => {
        if (suspendModalView === 'success') {
            handleCloseSuspendModal();
            return;
        }
        suspendForm.submit();
    };

    const handleCloseSuspendModal = () => {
        const wasSuccess = suspendModalView === 'success';
        setVisibleModal(false);
        setSelectedUserID(null);
        setSuspendModalView('form');
        setSuspendSuccessInfo(null);
        suspendForm.resetFields();
        // Closing the modal closes only the modal — the drawer stays open independently.
        if (wasSuccess) {
            getData();
        }
    };
    const handleCompanyChange = (companyId: any) => {
        console.log("select");
        console.log(companyId);
        const companyIDs = []
        if (companyId) {
            companyIDs.push(companyId);
        }
    };

    return (
        <>
            <PageHeader onExport={handleExport} />
            <InsightStyles />

            <StatsCards
                animatedUsers={animatedCount.users}
                animatedAmount={animatedCount.amount}
                trendPoints={trendPoints}
                statusSlices={statusSlices}
            />

            <FiltersBar
                form={form}
                activeSegment={activeSegment}
                statuses={statuses}
                hrCompanyGroupsInfo={hrCompanyGroupsInfo}
                canResetFilter={canResetFilter}
                onSearch={handleSearch}
                onSegmentChange={handleSegmentChange}
                onStatusChange={handleStatusSelectChange}
                onClearFilter={handleClearFilter}
                onCompanyChange={handleCompanyChange}
            />

            {/* User Details section header */}
            <div style={{
                display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                gap: 12, flexWrap: 'wrap', marginBottom: 10,
            }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: BRAND.ink }}>User Details</span>
                <span style={{ fontSize: 12, color: '#7c8b91' }}>
                    Showing {users?.length ?? 0} of {pagination.total ?? 0}
                </span>
            </div>

            <div className="np-modern-table">
                <ReusableDataTable
                    columns={columns} rows={users}
                    pagination={pagination} loading={loadingData} scrolling={{ x: 'max-content' }} handleTableChange={handleTableChange} hiddenRefreshing={true}
                    onRow={onRow}
                />
            </div>

            <SuspendModal
                visible={visibleModal}
                view={suspendModalView}
                suspending={suspending}
                loadingReasons={loadingReasons}
                suspensionReasons={suspensionReasons}
                successInfo={suspendSuccessInfo}
                form={suspendForm}
                onOk={handleModalOk}
                onCancel={handleCloseSuspendModal}
                onFinish={handleSuspendUser}
            />

            <UserDetailsDrawer
                open={!!detailsUser}
                user={detailsUser}
                onClose={() => setDetailsUser(null)}
                onSuspend={handleSuspendFromDrawer}
            />
        </>
    );
}
